import { Button, Card } from "cdhs-ui-library";
import { useNavigate } from "react-router";
import { ROUTES } from '../../config/routes';

//Icons and images
import IMAGE from "../../config/imgConfig";
import { FiArrowRight } from "react-icons/fi";

import BottomCards from "../../components/BottomCards/BottomCards";
import styles from "./home.module.scss";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { useTranslations } from "../../contexts/LanguageContext";
import { auth } from "../../store/user/userSlice";

interface HomeProps {
  incentive: string | undefined;
}

function Home({ incentive = "$" }: HomeProps) {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector(state => state.user.user);
  const loading = useAppSelector(state => state.user.loading);
  const authError = useAppSelector(state => state.user.error);

  // Read launch parameters and flag from Redux Store
  const clientId = useAppSelector(state => state.user.clientId);
  const surveyType = useAppSelector(state => state.user.surveyType);
  const isDashboardLaunch = useAppSelector(state => state.user.isDashboardLaunch);

  // Determine the default path if not a dashboard launch or already logged in
  let buttonPath = ROUTES.SURVEY_START;

  const handleTakeSurveyClick = async (event: React.MouseEvent) => {
    event.preventDefault();

    if (loading) return;

    if (user) {
      // User is already logged in, navigate directly
      navigate(ROUTES.SURVEY_START);
    } else if (isDashboardLaunch && clientId) {
      // Dashboard Launch: Skip Login page, attempt immediate authentication (pre-auth)

      // We must convert the string URL parameters to the number type expected by the API
      const numericClientId = clientId ? parseInt(clientId, 10) : undefined;
      const numericSurveyType = surveyType ? parseInt(surveyType, 10) : undefined;
      
      if (numericClientId) {
        await dispatch(auth({
          LoginAttempt: true,
          ClientId: numericClientId,
          SurveyType: numericSurveyType
        }));
        navigate(ROUTES.SURVEY_START);
      } else {
        console.error("Dashboard launch attempted without a valid Client ID.");
      }
    } else {
      // Standard Flow: Navigate to login page using the calculated buttonPath
      navigate(buttonPath);
    }
  };

  const homeHeroMobileAlt = t('alt_homeHeroMobile', IMAGE.HOME_HERO_MOBILE.alt);

  return (
    <div className={styles.mainContainer}>
      <section className={styles.hero}>
        <img src={IMAGE.HOME_HERO_MOBILE.url} alt={homeHeroMobileAlt} className={styles.heroMobile} />
        <img
          src={IMAGE.HOME_HERO_DESKTOP.url}
          alt={homeHeroMobileAlt}
          className={`${styles[`heroImg`]}`}
        />
        <div className={styles.heroText}>
          <h1 className={styles.titleHero}>{t('homePage_titleHero')}</h1>
          <p>{t('homePage_heroParagraph1', "Help us improve support for young people transitioning from foster care to adulthood. Your answers will help us plan better programs and benefits.")}</p>
          <p>{t('homePage_preParagraph2', "Bonus! Get a ")}{incentive}{t('homePage_heroParagraph2', " incentive for completing the survey. We'll also provide a list of resources you might be eligible for.")}</p>

          {authError && isDashboardLaunch && !user && (
              <p style={{ color: 'red', fontWeight: 'bold' }}>
                  {t('error_dashboardLogin', `Login failed: `)} {authError}
              </p>
          )}
          {/*Button handler */}
          <div onClick={handleTakeSurveyClick} role="button" tabIndex={0} className={styles.takeSurveyButtonContainer}>
            <Button
              caption={t('homePage_buttonTakeSurvey', 'Take the survey')}
              variant="NYTD"
              fullWidth
              iconPosition="Right"
              image={<FiArrowRight />}
            />
          </div>
        </div>
      </section>

      <section className={styles.stepsContent}>
        <h2>{t('homePage_incentiveTitle', "Get an incentive!")}</h2>
        <h3 className={styles.incentiveAmounts}>{t('homePage_incentiveAmounts', "Get $25, $35, or $75")}</h3>
        <p>{t('homePage_incentiveInfo', "Each time you participate in the survey you will receive an incentive: $25 at age 17, $35 at age 19, and $75 at age 21")}</p>
        <h3>
          <strong>{t('homePage_howItWorksTitle', "How does it work?")}</strong>
        </h3>
        <div className={styles.cardContainer}>
          <Card
            header={t('homePage_card1Header', "1. Complete Survey")}
            image={{ src: IMAGE.HOME_ICON_DEVICE.url, imgAlt: t('alt_homeIconDevice', IMAGE.HOME_ICON_DEVICE.alt) }}
            content={t('homePage_card1Content', "Log in with your info and complete and submit the survey.")}
            variant="NYTD"
          />
          <Card
            header={t('homePage_card2Header', "2. Get Incentive $")}
            image={{ src: IMAGE.HOME_ICON_RIBBON.url, imgAlt: t('alt_homeIconRibbon', IMAGE.HOME_ICON_RIBBON.alt) }}
            content={t('homePage_card2Content', "After you complete and submit the survey, you'll get instructions on how to receive your incentive")}
            variant="NYTD"
          />
          <Card
            header={t('homePage_card3Header', "3. Follow up survey")}
            image={{ src: IMAGE.HOME_ICON_COMMENTS.url, imgAlt: t('alt_homeIconComments', IMAGE.HOME_ICON_COMMENTS.alt) }}
            content={t('homePage_card3Content', "To qualify for the next incentive complete the follow up survey at ages 19 and 21. You will receive a reminder around your birthday.")}
            variant="NYTD"
          />
        </div>
      </section>

      <BottomCards />
    </div>
  );
}

export default Home;